import React, { useEffect, useState, useCallback } from "react";
import GenericModal from "./GenericModal";
import { fetchDailySummary } from "../../services/eventService";
import useUserStore from "../../stores/userStore";
import useModalStore from "../../stores/modalStore";
import { DollarSign, Clock, Dog, TrendingUp, MapPin, ChevronDown, ChevronRight, ChevronLeft, AlertCircle, Calendar, CalendarClock } from "lucide-react";

const StatCard = ({ icon: Icon, label, value, subValue, color = "blue" }) => {
    const colorClasses = {
        blue: "bg-blue-50 text-blue-700 border-blue-200",
        green: "bg-green-50 text-green-700 border-green-200",
        purple: "bg-purple-50 text-purple-700 border-purple-200",
        amber: "bg-amber-50 text-amber-700 border-amber-200",
    };
    return (
        <div className={`rounded-xl border p-4 ${colorClasses[color]}`}>
            <div className="flex items-center gap-2 mb-1">
                <Icon size={18} />
                <span className="text-sm font-medium opacity-80">{label}</span>
            </div>
            <div className="text-2xl font-bold">{value}</div>
            {subValue && <div className="text-xs opacity-70 mt-0.5">{subValue}</div>}
        </div>
    );
};

const LocationSection = ({ location, defaultExpanded = false }) => {
    const [expanded, setExpanded] = useState(defaultExpanded);
    const Arrow = expanded ? ChevronDown : ChevronRight;
    const proj = location.projected;

    return (
        <div className="border border-gray-200 rounded-xl overflow-hidden">
            <button
                onClick={() => setExpanded(!expanded)}
                className="w-full flex items-center justify-between px-4 py-3 bg-gray-50 hover:bg-gray-100 transition-colors"
            >
                <div className="flex items-center gap-2">
                    <MapPin size={16} className="text-gray-500" />
                    <span className="font-semibold text-gray-800">{location.location_name}</span>
                </div>
                <div className="flex items-center gap-4 text-sm text-gray-600">
                    <span>{location.hours_worked}h</span>
                    <span>${location.revenue.toLocaleString()}</span>
                    <span>{location.pet_count} pets</span>
                    <Arrow size={16} />
                </div>
            </button>

            {expanded && (
                <div className="px-4 py-3 space-y-3">
                    {location.error && (
                        <div className="flex items-center gap-2 text-amber-600 text-sm bg-amber-50 rounded-lg px-3 py-2">
                            <AlertCircle size={14} />
                            <span>DaySmart data unavailable: {location.error}</span>
                        </div>
                    )}

                    {proj && (
                        <div className="bg-amber-50 border border-amber-200 rounded-lg px-3 py-2">
                            <div className="flex items-center gap-1.5 mb-1.5">
                                <CalendarClock size={13} className="text-amber-600" />
                                <span className="text-xs font-semibold text-amber-700">Projected</span>
                            </div>
                            <div className="grid grid-cols-2 sm:grid-cols-4 gap-2 text-sm">
                                <div>
                                    <span className="text-amber-700 font-bold">{proj.appointment_count}</span>
                                    <span className="text-amber-600 text-xs ml-1">appts</span>
                                </div>
                                <div>
                                    <span className="text-amber-700 font-bold">{proj.pet_count}</span>
                                    <span className="text-amber-600 text-xs ml-1">pets</span>
                                </div>
                                <div>
                                    <span className="text-amber-700 font-bold">${proj.estimated_revenue?.toLocaleString() ?? 0}</span>
                                    <span className="text-amber-600 text-xs ml-1">est. rev</span>
                                </div>
                                <div>
                                    <span className="text-amber-700 font-bold">{proj.estimated_hours ?? 0}h</span>
                                    <span className="text-amber-600 text-xs ml-1">est. hours</span>
                                </div>
                            </div>
                        </div>
                    )}

                    <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
                        <StatCard icon={Clock} label="Hours Worked" value={`${location.hours_worked}h`} color="blue" />
                        <StatCard icon={DollarSign} label="Revenue" value={`$${location.revenue.toLocaleString()}`} color="green" />
                        <StatCard icon={TrendingUp} label="Tips" value={`$${location.tip_total.toLocaleString()}`} color="purple" />
                        <StatCard icon={Dog} label="Pets Serviced" value={location.pet_count} subValue={`${location.ticket_count} tickets`} color="amber" />
                    </div>

                    {location.employee_hours?.length > 0 && (
                        <div>
                            <h4 className="text-sm font-semibold text-gray-600 mb-2">Employee Hours</h4>
                            <div className="space-y-1">
                                {location.employee_hours.map((emp) => (
                                    <div key={emp.user_id} className="flex justify-between text-sm px-2 py-1 bg-gray-50 rounded">
                                        <span className="text-gray-700">{emp.name}</span>
                                        <span className="font-medium text-gray-800">{emp.hours}h</span>
                                    </div>
                                ))}
                            </div>
                        </div>
                    )}
                </div>
            )}
        </div>
    );
};

// Get date string in America/Chicago timezone (salon timezone)
const getTodayCentral = () => {
    return new Date().toLocaleDateString("en-CA", { timeZone: "America/Chicago" });
};

const toISODate = (d) => {
    // Use Central time formatting to avoid UTC date shift
    return d.toLocaleDateString("en-CA", { timeZone: "America/Chicago" });
};

const ProjectedBanner = ({ projected }) => {
    if (!projected) return null;
    return (
        <div className="bg-gradient-to-r from-amber-50 to-orange-50 border border-amber-200 rounded-xl p-4">
            <div className="flex items-center gap-2 mb-2">
                <CalendarClock size={16} className="text-amber-600" />
                <span className="text-sm font-semibold text-amber-700">Projected (Scheduled Appointments)</span>
            </div>
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
                <div>
                    <div className="text-xl font-bold text-amber-700">{projected.appointment_count}</div>
                    <div className="text-xs text-amber-600">Appointments</div>
                </div>
                <div>
                    <div className="text-xl font-bold text-amber-700">{projected.pet_count}</div>
                    <div className="text-xs text-amber-600">Pets Scheduled</div>
                </div>
                <div>
                    <div className="text-xl font-bold text-amber-700">${projected.estimated_revenue?.toLocaleString() ?? 0}</div>
                    <div className="text-xs text-amber-600">Est. Revenue</div>
                </div>
                <div>
                    <div className="text-xl font-bold text-amber-700">{projected.estimated_hours ?? 0}h</div>
                    <div className="text-xs text-amber-600">Est. Hours</div>
                </div>
            </div>
        </div>
    );
};

const DailySummaryModal = () => {
    const { currentUser } = useUserStore();
    const showDailySummaryModal = useModalStore((state) => state.showDailySummaryModal);
    const [summary, setSummary] = useState(null);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);
    const [selectedDate, setSelectedDate] = useState(getTodayCentral());

    const loadSummary = useCallback((dateStr) => {
        if (!currentUser?.user_id) return;
        setLoading(true);
        setError(null);
        fetchDailySummary(dateStr, currentUser.user_id)
            .then((data) => setSummary(data))
            .catch((err) => setError(err?.response?.data?.error || err.message))
            .finally(() => setLoading(false));
    }, [currentUser?.user_id]);

    // Load on open or date change
    useEffect(() => {
        if (showDailySummaryModal && currentUser?.user_id) {
            loadSummary(selectedDate);
        }
    }, [showDailySummaryModal, selectedDate, currentUser?.user_id, loadSummary]);

    // Reset to today when modal opens
    useEffect(() => {
        if (showDailySummaryModal) {
            setSelectedDate(getTodayCentral());
        }
    }, [showDailySummaryModal]);

    const goDay = (offset) => {
        const d = new Date(selectedDate + "T12:00:00");
        d.setDate(d.getDate() + offset);
        setSelectedDate(toISODate(d));
    };

    const goToToday = () => setSelectedDate(getTodayCentral());

    const formatDate = (dateStr) => {
        if (!dateStr) return "";
        const d = new Date(dateStr + "T12:00:00");
        return d.toLocaleDateString("en-US", { weekday: "long", month: "long", day: "numeric", year: "numeric" });
    };

    const isToday = selectedDate === getTodayCentral();

    return (
        <GenericModal
            name="dailySummaryModal"
            title="Daily Business Summary"
            hideButton={true}
            modalMaxWidth="max-w-3xl"
        >
            <div className="p-6 space-y-5">
                {/* Date navigation */}
                <div className="flex items-center justify-center gap-3 -mt-2">
                    <button
                        onClick={() => goDay(-1)}
                        disabled={loading}
                        className="p-1.5 rounded-lg border border-gray-200 hover:bg-gray-100 hover:border-gray-300 text-gray-500 hover:text-gray-700 transition-colors disabled:opacity-40"
                        title="Previous day"
                    >
                        <ChevronLeft size={18} />
                    </button>

                    <div className="flex items-center gap-2 min-w-[240px] justify-center">
                        {summary?.is_future && (
                            <span className="px-2 py-0.5 text-[10px] font-bold uppercase tracking-wider bg-amber-100 text-amber-700 rounded-full">
                                Projected
                            </span>
                        )}
                        {summary?.is_today && !summary?.is_future && (
                            <span className="px-2 py-0.5 text-[10px] font-bold uppercase tracking-wider bg-blue-100 text-blue-700 rounded-full">
                                Today
                            </span>
                        )}
                        <span className="text-sm font-medium text-gray-700">{formatDate(selectedDate)}</span>
                    </div>

                    <button
                        onClick={() => goDay(1)}
                        disabled={loading}
                        className="p-1.5 rounded-lg border border-gray-200 hover:bg-gray-100 hover:border-gray-300 text-gray-500 hover:text-gray-700 transition-colors disabled:opacity-40"
                        title="Next day"
                    >
                        <ChevronRight size={18} />
                    </button>

                    {!isToday && (
                        <button
                            onClick={goToToday}
                            disabled={loading}
                            className="ml-1 px-2.5 py-1 text-xs font-medium text-indigo-600 bg-indigo-50 border border-indigo-200 rounded-lg hover:bg-indigo-100 transition-colors disabled:opacity-40"
                            title="Go to today"
                        >
                            <Calendar size={12} className="inline mr-1" />
                            Today
                        </button>
                    )}
                </div>

                {loading && (
                    <div className="flex items-center justify-center py-12">
                        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600" />
                        <span className="ml-3 text-gray-500">Loading summary...</span>
                    </div>
                )}

                {error && (
                    <div className="flex items-center gap-2 text-red-600 bg-red-50 rounded-lg px-4 py-3">
                        <AlertCircle size={18} />
                        <span>{error}</span>
                    </div>
                )}

                {summary && !loading && (
                    <>
                        {/* Projected banner for future/today */}
                        {summary.projected && <ProjectedBanner projected={summary.projected} />}

                        {/* Combined totals */}
                        <div className={`rounded-xl p-5 text-white ${summary.is_future ? "bg-gradient-to-r from-amber-500 to-orange-500" : "bg-gradient-to-r from-blue-600 to-indigo-600"}`}>
                            <h3 className="text-sm font-medium opacity-80 mb-3">
                                All Locations Combined {summary.is_future ? "(Closed Tickets)" : ""}
                            </h3>
                            <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
                                <div>
                                    <div className="text-2xl font-bold">{summary.totals.hours_worked}h</div>
                                    <div className="text-xs opacity-70">Hours Worked</div>
                                </div>
                                <div>
                                    <div className="text-2xl font-bold">${summary.totals.revenue.toLocaleString()}</div>
                                    <div className="text-xs opacity-70">Revenue</div>
                                </div>
                                <div>
                                    <div className="text-2xl font-bold">${summary.totals.tip_total.toLocaleString()}</div>
                                    <div className="text-xs opacity-70">Tips</div>
                                </div>
                                <div>
                                    <div className="text-2xl font-bold">{summary.totals.pet_count}</div>
                                    <div className="text-xs opacity-70">Pets Serviced</div>
                                </div>
                            </div>
                        </div>

                        {/* Per-location breakdown */}
                        <div className="space-y-3">
                            {summary.locations.map((loc) => (
                                <LocationSection key={loc.location_id} location={loc} defaultExpanded={true} />
                            ))}
                        </div>
                    </>
                )}
            </div>
        </GenericModal>
    );
};

export default DailySummaryModal;
